import { useEffect } from 'react';
import { CustomTableProps } from '../../../interfaces';

/**
 * This hook automatically overlays a semi-transparent loading indicator on table rows
 * with the 'loading-row' class inside an Ant Design table. It observes DOM mutations
 * and table body resizing to ensure overlays are correctly positioned and sized.
 * Useful for visually indicating loading states at the row level in custom tables.
 */
const useLoadingRow = (
  tableRef: React.RefObject<HTMLDivElement>,
  dataSource: CustomTableProps['dataSource'],
) => {
  useEffect(() => {
    if (!tableRef?.current) return;

    const tableBody = tableRef.current.querySelector('.ant-table-tbody');
    if (!tableBody) return;

    const addLoadingOverlay = () => {
      const selections = tableBody.getElementsByClassName(
        'ant-table-selection-column',
      );
      Array.from(selections).forEach(selection => {
        const selectionCell = selection as HTMLElement;
        const existedOverlay = selectionCell.getElementsByClassName(
          'loading-row-overlay',
        );
        if (existedOverlay.length) return;

        const overlay = document.createElement('div');
        overlay.className = 'loading-row-overlay';
        overlay.style.width = `${tableBody.clientWidth}px`;

        // Make sure the cell is positioned so the overlay can anchor properly
        const computed = getComputedStyle(selectionCell);
        if (computed.position === 'static') overlay.style.position = 'relative';
        selectionCell.appendChild(overlay);
      });
    };

    // Schedules addLoadingOverlay to run soon
    const raf = requestAnimationFrame(addLoadingOverlay);

    return () => cancelAnimationFrame(raf);
  }, [tableRef, dataSource]);
};

export default useLoadingRow;
